import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaPencilAlt, FaEye, FaSearch, FaTh, FaBars, FaTimes, FaUser, FaMoon, FaSun, FaDownload,FaPlus } from 'react-icons/fa';
import SummaryApi from '../common/index';
import { CiUser } from "react-icons/ci";
const LoaderSVG = () => (
  <svg
    className="animate-spin h-10 w-10 text-blue-600 dark:text-blue-400 mx-auto"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

const InstructorList = () => {
  const [instructors, setInstructors] = useState([]);
  const [filteredInstructors, setFilteredInstructors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [viewMode, setViewMode] = useState('grid');
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    job: '',
    email: '',
    mobile: '',
    rating: '',
  });
  const [showFilters, setShowFilters] = useState({
    name: false,
    job: false,
    email: false,
    mobile: false,
    rating: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const itemsPerPage = 8;

  useEffect(() => {
    const updateTheme = () => {
      const adminTheme = sessionStorage.getItem('admin-theme');
      const shouldBeDark = adminTheme === 'dark';
      setIsDarkMode(shouldBeDark);
      document.documentElement.classList.toggle('dark', shouldBeDark);
    };

    updateTheme();

    const handleThemeChange = () => updateTheme();
    const handleStorageChange = (e) => { if (e.key === 'admin-theme') updateTheme(); };
    const handleAdminThemeToggled = () => updateTheme();

    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adminThemeToggled', handleAdminThemeToggled);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminThemeToggled', handleAdminThemeToggled);
    };
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    sessionStorage.setItem('admin-theme', newDarkMode ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', newDarkMode);
    window.dispatchEvent(new Event('themeChanged'));
    window.dispatchEvent(new Event('adminThemeToggled'));
  };

  const formatImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = SummaryApi.Images.url.replace('/api', '');
    if (imagePath.includes(baseUrl)) return imagePath;
    if (imagePath.startsWith('uploads/')) return `${baseUrl}/${imagePath}`;
    return `${baseUrl}/${imagePath}`;
  };

  useEffect(() => {
    fetchInstructors();
    window.addEventListener('focus', fetchInstructors);
    return () => window.removeEventListener('focus', fetchInstructors);
  }, []);

  useEffect(() => {
    if (instructors.length > 0) {
      let filtered = instructors.filter(instructor => {
        const matchesActiveStatus = instructor.is_active === (activeTab === 'active' ? 1 : 0);
        const allDocumentsNull =
          instructor.document1 === null &&
          instructor.document2 === null &&
          instructor.document3 === null &&
          instructor.document4 === null &&
          instructor.document5 === null;
        return matchesActiveStatus && !allDocumentsNull;
      });

      if (searchTerm) {
        filtered = filtered.filter(instructor =>
          `${instructor.first_name} ${instructor.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
          instructor.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          instructor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          instructor.mobile_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (instructor.rating && instructor.rating.toString().includes(searchTerm))
        );
      }

      filtered = filtered.filter(instructor =>
        (`${instructor.first_name} ${instructor.last_name}`.toLowerCase().includes(columnFilters.name.toLowerCase()) || !columnFilters.name) &&
        (instructor.job_title?.toLowerCase().includes(columnFilters.job.toLowerCase()) || !columnFilters.job) &&
        (instructor.email.toLowerCase().includes(columnFilters.email.toLowerCase()) || !columnFilters.email) &&
        (instructor.mobile_number?.toLowerCase().includes(columnFilters.mobile.toLowerCase()) || !columnFilters.mobile) &&
        (columnFilters.rating ? (instructor.rating ? parseFloat(instructor.rating) >= parseFloat(columnFilters.rating) : false) : true)
      );

      setFilteredInstructors(filtered);
      setCurrentPage(1);
    }
  }, [activeTab, instructors, searchTerm, columnFilters]);

  const fetchInstructors = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(SummaryApi.instructor_retrieve_all_for_activation.url, {
        method: SummaryApi.instructor_retrieve_all_for_activation.method,
      });
      const data = await response.json();

      if (data.status) {
        const instructorsWithDocuments = (data.data || []).filter(instructor => {
          const allDocumentsNull =
            instructor.document1 === null &&
            instructor.document2 === null &&
            instructor.document3 === null &&
            instructor.document4 === null &&
            instructor.document5 === null;
          return !allDocumentsNull;
        });
        setInstructors(instructorsWithDocuments);
      } else {
        toast.error(data.message || 'Failed to fetch instructors');
      }
    } catch (error) {
      console.error('Error fetching instructors:', error);
      toast.error('Error fetching instructors');
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivation = (instructor) => {
    setSelectedInstructor(instructor);
    setShowActivationModal(true);
  };

  const confirmActivation = async () => {
    if (!selectedInstructor) return;
    try {
      const response = await fetch(SummaryApi.InstructorActivation.url, {
        method: SummaryApi.InstructorActivation.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instructor_id: selectedInstructor.instructor_id,
          is_active: 1,
        }),
      });
      const data = await response.json();
      if (data.status) {
        toast.success('Instructor activated successfully!');
        fetchInstructors();
      } else {
        toast.error(data.message || 'Failed to activate instructor');
      }
    } catch (error) {
      console.error('Error activating instructor:', error);
      toast.error('Error activating instructor');
    } finally {
      setShowActivationModal(false);
      setSelectedInstructor(null);
    }
  };

  const handleEdit = (instructor) => {
    const encodedId = btoa(instructor.instructor_id.toString());
    window.open(`/admin-panel/create-instructor?id=${encodedId}`, '_blank');
  };

  const handleView = (instructor) => {
    const encodedId = btoa(instructor.instructor_id.toString());
    window.open(`/admin-panel/instructor-details?id=${encodedId}`, '_blank');
  };

  const renderRating = (rating) => {
    let effectiveRating = parseFloat(rating);
    if (isNaN(effectiveRating) || effectiveRating < 0) effectiveRating = 0;
    if (effectiveRating > 5) effectiveRating = 5;

    const fullStars = Math.floor(effectiveRating);
    const hasHalfStar = effectiveRating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center">
        {[...Array(fullStars)].map((_, i) => (
          <svg key={`full-${i}`} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        {hasHalfStar && (
          <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <svg key={`empty-${i}`} className="w-4 h-4 text-gray-300 dark:text-gray-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-sm text-gray-700 dark:text-gray-300">({effectiveRating})</span>
      </div>
    );
  };

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentInstructors = filteredInstructors.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredInstructors.length / itemsPerPage);

  const downloadCSV = () => {
    const headers = ['Name', 'Job Title', 'Email', 'Mobile', 'Rating'];
    const rows = filteredInstructors.map(instructor => [
      `${instructor.first_name} ${instructor.last_name}`,
      instructor.job_title || 'N/A',
      instructor.email || 'N/A',
      instructor.mobile_number || 'N/A',
      instructor.rating || 'N/A'
    ]);

    let csvContent = "data:text/csv;charset=utf-8," +
      headers.join(",") + "\n" +
      rows.map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "instructors.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFilter = (key) => {
    setShowFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleReset = () => {
    setSearchTerm('');
    setColumnFilters({ name: '', job: '', email: '', mobile: '', rating: '' });
    setShowFilters({ name: false, job: false, email: false, mobile: false, rating: false });
  };

  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () => setCurrentPage(totalPages);
  const handlePrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto w-full flex-1 px-4 sm:px-6 lg:px-8 py-6 lg:py-8">

        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className={`text-2xl sm:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Instructor Management
          </h1>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
              className={`p-2.5 rounded-lg border transition-all ${viewMode === 'grid' ? 'bg-[#1f4774] hover:bg-blue-700 text-white' : isDarkMode ? 'text-blue-400 border-gray-600 hover:bg-gray-800' : 'text-blue-900 border-gray-300 hover:bg-gray-100'}`}
              title="Grid View"
            >
              <FaTh size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              aria-label="List view"
              className={`p-2.5 rounded-lg border transition-all ${viewMode === 'list' ? 'bg-[#1f4774] hover:bg-blue-700 text-white' : isDarkMode ? 'text-blue-400 border-gray-600 hover:bg-gray-800' : 'text-blue-700 border-gray-300 hover:bg-gray-100'}`}
              title="List View"
            >
              <FaBars size={16} />
            </button>
            <button
              onClick={downloadCSV}
              disabled={filteredInstructors.length === 0}
              className={`px-5 py-2.5 rounded-lg font-medium text-white flex items-center gap-2 transition-all shadow-md ${filteredInstructors.length === 0 ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#1f4774] hover:bg-blue-700'}`}
            >
              <FaDownload /> Download Excel
            </button>
          </div>
        </div>

        {/* Main Card */}
        <div className={`rounded-2xl shadow-xl border overflow-hidden flex flex-col flex-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>

          {/* Tabs + Search */}
          <div className={`px-6 py-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex">
              <button onClick={() => setActiveTab('active')} className={`px-6 py-1 font-medium rounded-t-lg transition-all ${activeTab === 'active' ? 'bg-[#1f4774] text-white' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                Active Instructors
              </button>
              <button onClick={() => setActiveTab('inactive')} className={`px-6 py-1 font-medium rounded-t-lg transition-all ${activeTab === 'inactive' ? 'bg-[#1f4774] text-white' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                Inactive Instructors
              </button>
            </div>

            <div className="flex items-center gap-1 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-56">
                <input
                  type="text"
                  placeholder="Global Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
                <FaSearch className="absolute left-3 top-2/4 -translate-y-1/2 text-gray-500" size={16} />
              </div>
              {(searchTerm || Object.values(columnFilters).some(f => f !== '')) && (
                <button onClick={handleReset} className="p-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-all">
                  <FaTimes size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-hidden">
            {isLoading ? (
              viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="bg-gray-200 dark:bg-gray-700 h-72 rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="flex justify-center items-center h-96">
                  <LoaderSVG />
                </div>
              )
            ) : filteredInstructors.length === 0 ? (
         <div className="flex flex-col items-center justify-center h-96 text-center">
    <FaUser className={`text-6xl mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
    <p className={`text-xl mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
      No {activeTab === 'active' ? 'active' : 'inactive'} instructors found for selected criteria.
    </p>
    <button
onClick={() => window.location.href = `${window.location.origin}/admin-panel/create-instructor`}
      className={`px-6 py-3 rounded-md font-medium transition-all shadow-lg flex items-center gap-2 ${
        isDarkMode
          ? "bg-blue-600 hover:bg-blue-700 text-white"
          : "bg-blue-900 hover:bg-blue-800 text-white"
      }`}
    >
      <FaPlus />
      Add Instructor
    </button>
  </div>
            ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {currentInstructors.map((instructor, index) => (
                  <div
                    key={instructor.id || index}
                    className={`
                      rounded-2xl shadow-lg overflow-hidden border-2 hover:shadow-2xl cursor-pointer
                      transition-all duration-300 flex flex-col h-full
                      ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 hover:border-blue-500"
                          : "bg-white border-gray-200 hover:border-blue-400"
                      }
                    `}
                    onClick={() => handleView(instructor)}
                  >
                    {/* Card Content */}
                <div className="p-6 flex flex-col flex-grow">
                      {/* Name + Badge */}
                      <h3
                        className={`font-bold text-[12px] flex items-center gap-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {instructor.is_self_registered === 1 ? (
                          <span className="bg-green-500 text-white px-2 py-0.5 rounded-full text-[10px] font-medium">
                            Self
                          </span>
                        ) : (
                          <span className="bg-blue-500 text-white px-2 py-0.5 rounded-full text-[10px] font-medium">
                            Admin
                          </span>
                        )}
                      </h3>
                      {/* Profile Image + Edit */}
                      <div className="mb-5 mt-3 relative text-center">
                        <div className="relative inline-block">
                          {instructor.instructor_image ? (
                            <img
                              src={formatImageUrl(instructor.instructor_image)}
                              alt={`${instructor.first_name} ${instructor.last_name}`}
                              className="w-28 h-28 rounded-full object-cover border-4 border-blue-100 dark:border-blue-900 shadow-md"
                            />
                          ) : (
                            <div className="w-28 h-28 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shadow-md">
                              <FaUser className="text-5xl text-gray-400" />
                            </div>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(instructor);
                            }}
                            className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                          >
                            <FaPencilAlt
                              size={14}
                              className="text-gray-600 dark:text-gray-300"
                            />
                          </button>
                        </div>
                      </div>
                      <p className="text-center mb-2 font-bold">
 {instructor.first_name} {instructor.last_name}
                      </p>
                      {/* Title */}
                      <p
                        className={`text-sm px-4 py-1.5 rounded-full font-medium text-center ${
                          isDarkMode
                            ? "bg-blue-900/50 text-blue-300"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {instructor.job_title || "No Title"}
                      </p>
                      {/* Info */}
                      <div
                        className={`text-sm space-y-2 mt-4 flex-start ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        <p className="">
                          <strong>Email:</strong> {instructor.email || "N/A"}
                        </p>
                        <p>
                          <strong>Mobile:</strong>{" "}
                          {instructor.mobile_number || "N/A"}
                        </p>
                        {/* Rating */}
                        <p>
                          {instructor.rating ? (
                            renderRating(instructor.rating)
                          ) : (
                            <span className="text-gray-500 text-sm">
                              No rating yet
                            </span>
                          )}
                        </p>
                      </div>
                      {/* Bottom Actions */}
                      <div className="w-full mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView(instructor);
                          }}
                          className="text-[#1F4774] dark:text-blue-600 hover:text-blue-700 dark:hover:text-blue-700 font-medium flex items-center justify-center gap-2 transition"
                        >
                          <FaEye size={16} /> View More
                        </button>
                        {activeTab === "inactive" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActivation(instructor);
                            }}
                            className="w-full py-1 bg-[#1F4774] dark:bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-700 text-white rounded-lg font-semibold transition"
                          >
                            Activate Instructor
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* List / Table View */
              <div className="overflow-x-auto">
                <table className={`min-w-full border-collapse border text-xs sm:text-sm transition-colors duration-300 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <thead className={`sticky top-0 z-10 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`border py-3 px-4 font-semibold text-center ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'}`}>Image</th>
                      <th className={`border py-3 px-4 font-semibold text-center ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'}`}>
                        <div className="flex flex-col items-center gap-1">
                          Name
                          <div className="flex items-center gap-1">
                            <button onClick={() => toggleFilter('name')} className={`transition-colors ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>
                              <FaSearch size={10} />
                            </button>
                            {showFilters.name && (
                              <input
                                type="text"
                                placeholder="Filter Name"
                                value={columnFilters.name}
                                onChange={(e) => setColumnFilters({ ...columnFilters, name: e.target.value })}
                                className={`w-24 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500' : 'border-gray-300 focus:ring-blue-500'}`}
                              />
                            )}
                          </div>
                        </div>
                      </th>
                      <th className={`border py-3 px-4 font-semibold text-center ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'}`}>Job Title</th>
                      <th className={`border py-3 px-4 font-semibold text-center ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'}`}>Email</th>
                      <th className={`border py-3 px-4 font-semibold text-center ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'}`}>Mobile</th>
                      <th className={`border py-3 px-4 font-semibold text-center ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'}`}>Rating</th>
                      <th className={`border py-3 px-4 font-semibold text-center ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-800'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentInstructors.map((instructor, index) => (
                      <tr key={instructor.id || index} className={`border-b hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <td className={`border py-3 px-4 text-center ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          {instructor.instructor_image ? (
                            <img
                              src={formatImageUrl(instructor.instructor_image)}
                              alt={`${instructor.first_name} ${instructor.last_name}`}
                              className={`h-10 w-10 object-cover rounded-full border-2 mx-auto ${isDarkMode ? 'border-blue-800' : 'border-blue-200'}`}
                            />
                          ) : (
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center border-2 mx-auto ${isDarkMode ? 'bg-gray-600 border-blue-800' : 'bg-gray-200 border-blue-200'}`}>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No Img</span>
                            </div>
                          )}
                        </td>

                        {/* Name + Self Tag in one cell – neatly aligned */}
                        <td className={`border py-3 px-4 text-center ${isDarkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            <span>{instructor.first_name} {instructor.last_name}</span>
                            {instructor.is_self_registered === 1 && (
                              <span className="bg-green-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                                Self
                              </span>
                            )}
                          </div>
                        </td>

                        <td className={`border py-3 px-4 text-center ${isDarkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                          {instructor.job_title || 'N/A'}
                        </td>
                        <td className={`border py-3 px-4 text-center ${isDarkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                          {instructor.email || 'N/A'}
                        </td>
                        <td className={`border py-3 px-4 text-center ${isDarkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                          {instructor.mobile_number || 'N/A'}
                        </td>
                        <td className={`border py-3 px-4 text-center ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          {instructor.rating ? renderRating(instructor.rating) : <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>N/A</span>}
                        </td>
                        <td className={`border py-3 px-4 text-center space-x-2 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <button onClick={() => handleView(instructor)} className={`p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 ${isDarkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'}`} title="View">
                            <FaEye size={14} />
                          </button>
                          <button onClick={() => handleEdit(instructor)} className={`p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 ${isDarkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'}`} title="Edit">
                            <FaPencilAlt size={14} />
                          </button>
                          {activeTab === 'inactive' && (
                            <button
                              onClick={() => handleActivation(instructor)}
                              className={`py-1 px-3 rounded-lg font-medium ${isDarkMode ? 'bg-blue-500 text-gray-900 hover:bg-blue-400' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                            >
                              Activate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={`px-6 py-4 border-t flex flex-col sm:flex-row justify-between items-center gap-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredInstructors.length)} of {filteredInstructors.length} instructors
              </span>
              <div className="flex gap-2">
                <button onClick={handleFirstPage} disabled={currentPage === 1} className="px-4 py-1 rounded-lg bg-blue-600 text-white disabled:bg-gray-600 hover:bg-blue-700 transition">First</button>
                <button onClick={handlePrevPage} disabled={currentPage === 1} className="px-4 py-1 rounded-lg bg-blue-600 text-white disabled:bg-gray-600 hover:bg-blue-700 transition">Prev</button>
                <span className="px-4 py-1 text-sm font-medium">Page {currentPage} of {totalPages}</span>
                <button onClick={handleNextPage} disabled={currentPage === totalPages} className="px-4 py-1 rounded-lg bg-blue-600 text-white disabled:bg-gray-600 hover:bg-blue-700 transition">Next</button>
                <button onClick={handleLastPage} disabled={currentPage === totalPages} className="px-4 py-1 rounded-lg bg-blue-600 text-white disabled:bg-gray-600 hover:bg-blue-700 transition">Last</button>
              </div>
            </div>
          )}
        </div>

        {/* Activation Modal */}
        {showActivationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`rounded-xl p-6 w-full max-w-md shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Confirm Activation</h3>
              <p className={`mb-6 text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to activate {selectedInstructor?.first_name} {selectedInstructor?.last_name}?
              </p>
              <div className="flex justify-end space-x-4">
                <button onClick={() => setShowActivationModal(false)} className={`px-4 py-2 border rounded-lg ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
                  Cancel
                </button>
                <button onClick={confirmActivation} className={`px-6 py-2 text-white rounded-lg font-medium ${isDarkMode ? 'bg-blue-500 hover:bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorList;